import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, FilePdf, MagnifyingGlass, X, Barcode } from "@phosphor-icons/react";
import { exportNutritionPDF } from "../../lib/pdf";
import MetabolicCalculator from "../../components/MetabolicCalculator";
import BarcodeScanner from "../../components/BarcodeScanner";
import FrequentFoods from "../../components/FrequentFoods";
import WeeklyNutritionAdherence from "../../components/WeeklyNutritionAdherence";

// Distribución calórica por tipo de comida (estándar Athlon)
const MEAL_DISTRIBUTION = [
  { key: "desayuno", label: "Desayuno", pct: 15, color: "#FFD60A" },
  { key: "almuerzo", label: "Almuerzo", pct: 35, color: "#FF3B30" },
  { key: "merienda", label: "Merienda", pct: 15, color: "#FFD60A" },
  { key: "cena", label: "Cena", pct: 35, color: "#FF3B30" },
];

// Infiere tipo de comida desde horario local (cliente). El backend también
// lo infiere si viene vacío; usamos este en el frontend como default para el
// selector visible en el formulario.
function inferMealTypeFromNow() {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return "desayuno";
  if (h >= 11 && h < 16) return "almuerzo";
  if (h >= 16 && h < 19) return "merienda";
  return "cena";
}

// Default macro split cuando el plan no tiene uno configurado
const DEFAULT_MACRO_SPLIT = { pct_carbs: 50, pct_protein: 25, pct_fat: 25 };

// Convierte % a gramos usando equivalencias energéticas (4 kcal/g HC y Prot, 9 kcal/g Grasa)
function macroGramsFromPct(kcal, pct, kcalPerGram) {
  if (!kcal || !pct) return 0;
  return Number(((kcal * pct / 100) / kcalPerGram).toFixed(1));
}

// Clasifica el nivel de adherencia de una comida vs su target calórico
// { status: "empty"|"ok"|"low"|"over"|"way_over", color, label }
function classifyMealAdherence(consumed, target) {
  if (!target || target <= 0) return { status: "empty", color: "#A3A3A3", label: "Sin objetivo" };
  if (consumed <= 0) return { status: "empty", color: "#A3A3A3", label: "Sin cargar" };
  const ratio = consumed / target;
  if (ratio < 0.5) return { status: "low", color: "#FFD60A", label: "Poco" };
  if (ratio <= 1.15) return { status: "ok", color: "#32D74B", label: "OK" };
  if (ratio <= 1.5) return { status: "over", color: "#FF9500", label: "Excedido" };
  return { status: "way_over", color: "#FF3B30", label: "Muy excedido" };
}

function MacroBar({ label, current, target, color }) {
  const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  return (
    <div data-testid={`macro-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-muted">{label}</span>
        <span className="font-display font-bold text-sm">
          <span style={{ color }}>{current.toFixed(0)}</span>
          <span className="text-muted"> / {target.toFixed(0)} g</span>
        </span>
      </div>
      <div className="h-2 bg-[#262626] relative overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function NutritionTab({ userId, isAdmin, isOwner, targetUser }) {
  const today = new Date().toISOString().slice(0, 10);
  const [plan, setPlan] = useState({ daily_calories: 0, daily_carbs_g: 0, daily_protein_g: 0, daily_fat_g: 0 });
  const [editingPlan, setEditingPlan] = useState(false);
  const [planForm, setPlanForm] = useState(plan);
  const [meals, setMeals] = useState([]);
  const [date, setDate] = useState(today);
  const [meal, setMeal] = useState({ meal_name: "", calories: "", carbs_g: "", protein_g: "", fat_g: "", meal_type: inferMealTypeFromNow() });
  const [catalog, setCatalog] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [servingG, setServingG] = useState(100); // gramos seleccionados cuando se elige del catálogo
  const [selectedMealType, setSelectedMealType] = useState(inferMealTypeFromNow()); // tipo seleccionado para la comida en el picker
  const [selectedFood, setSelectedFood] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [frequents, setFrequents] = useState([]);
  const [frequentsLoading, setFrequentsLoading] = useState(true);

  const loadFrequents = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/food-catalog/frequent`, { params: { limit: 6 } });
      setFrequents(data);
    } catch { /* noop */ } finally {
      setFrequentsLoading(false);
    }
  };

  useEffect(() => {
    loadFrequents();
    // eslint-disable-next-line
  }, [userId]);

  // Guarda silenciosamente un producto en el catálogo personal del atleta
  const rememberFood = async (food) => {
    try {
      await api.post(`/users/${userId}/food-catalog`, {
        name: food.name,
        brand: food.brand || null,
        image_url: food.image_url || null,
        barcode: food.barcode || null,
        serving_size_g: food.serving_size_g || 100,
        serving_hint_g: food.serving_hint_g || null,
        calories: food.calories,
        carbs_g: food.carbs_g || 0,
        protein_g: food.protein_g || 0,
        fat_g: food.fat_g || 0,
        category: food.category || null,
      });
      loadFrequents();
      loadCatalog();
    } catch { /* silencioso, no es crítico */ }
  };

  // Quick-add: agrega una comida del top frequentes al día actual sin abrir el picker
  const quickAddFrequent = async (food) => {
    const hintG = food.serving_hint_g || food.serving_size_g || 100;
    const factor = hintG / (food.serving_size_g || 100);
    const label = food.brand ? `${food.name} · ${food.brand} (${hintG}g)` : `${food.name} (${hintG}g)`;
    try {
      await api.post(`/users/${userId}/meals`, {
        date,
        meal_name: label,
        calories: Number((food.calories * factor).toFixed(1)),
        carbs_g: Number((food.carbs_g * factor).toFixed(1)),
        protein_g: Number((food.protein_g * factor).toFixed(1)),
        fat_g: Number((food.fat_g * factor).toFixed(1)),
        meal_type: inferMealTypeFromNow(),
      });
      toast.success(`${food.name} agregado`);
      loadMeals();
      rememberFood(food); // incrementa use_count
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadCatalog = () => {
    api.get("/food-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  };
  useEffect(() => { loadCatalog(); }, []);

  const loadPlan = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/nutrition-plan`);
      // Asegurar defaults de split cuando el plan aún no los tiene
      const withDefaults = {
        ...DEFAULT_MACRO_SPLIT,
        ...data,
        pct_carbs: data.pct_carbs ?? DEFAULT_MACRO_SPLIT.pct_carbs,
        pct_protein: data.pct_protein ?? DEFAULT_MACRO_SPLIT.pct_protein,
        pct_fat: data.pct_fat ?? DEFAULT_MACRO_SPLIT.pct_fat,
      };
      setPlan(withDefaults);
      setPlanForm(withDefaults);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadMeals = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/meals`, { params: { date } });
      setMeals(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { loadPlan(); /* eslint-disable-next-line */ }, [userId]);
  useEffect(() => { loadMeals(); /* eslint-disable-next-line */ }, [userId, date]);

  const savePlan = async (e) => {
    e.preventDefault();
    // Validación del split: los 3 % deben sumar 100
    const pc = Number(planForm.pct_carbs);
    const pp = Number(planForm.pct_protein);
    const pf = Number(planForm.pct_fat);
    const pctSum = pc + pp + pf;
    if (Math.abs(pctSum - 100) > 0.5) {
      toast.error(`Los porcentajes de macros deben sumar 100 (actual: ${pctSum.toFixed(0)}%)`);
      return;
    }
    const kcal = Number(planForm.daily_calories) || 0;
    // Recalculamos gramos desde % cuando el admin editó los %
    const carbsG = macroGramsFromPct(kcal, pc, 4);
    const proteinG = macroGramsFromPct(kcal, pp, 4);
    const fatG = macroGramsFromPct(kcal, pf, 9);
    try {
      const { data } = await api.put(`/users/${userId}/nutrition-plan`, {
        daily_calories: kcal,
        daily_carbs_g: carbsG,
        daily_protein_g: proteinG,
        daily_fat_g: fatG,
        pct_carbs: pc,
        pct_protein: pp,
        pct_fat: pf,
        waist_cm: plan.waist_cm ?? null,
        hip_cm: plan.hip_cm ?? null,
        activity_level: plan.activity_level ?? null,
        activities: plan.activities ?? null,
      });
      setPlan(data);
      setPlanForm(data);
      setEditingPlan(false);
      toast.success("Plan actualizado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Persiste cambios del calculador metabólico sin salir del estado del formulario principal
  const saveMetabolicPatch = async (patch) => {
    try {
      const { data } = await api.put(`/users/${userId}/nutrition-plan`, {
        daily_calories: Number(plan.daily_calories) || 0,
        daily_carbs_g: Number(plan.daily_carbs_g) || 0,
        daily_protein_g: Number(plan.daily_protein_g) || 0,
        daily_fat_g: Number(plan.daily_fat_g) || 0,
        pct_carbs: plan.pct_carbs ?? null,
        pct_protein: plan.pct_protein ?? null,
        pct_fat: plan.pct_fat ?? null,
        ...patch,
      });
      setPlan(data);
      setPlanForm(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const useAsGoal = async (kcal) => {
    // Al establecer nuevo objetivo calórico, recalcular gramos usando el split guardado (o default)
    const pc = Number(plan.pct_carbs) || DEFAULT_MACRO_SPLIT.pct_carbs;
    const pp = Number(plan.pct_protein) || DEFAULT_MACRO_SPLIT.pct_protein;
    const pf = Number(plan.pct_fat) || DEFAULT_MACRO_SPLIT.pct_fat;
    try {
      const { data } = await api.put(`/users/${userId}/nutrition-plan`, {
        daily_calories: kcal,
        daily_carbs_g: macroGramsFromPct(kcal, pc, 4),
        daily_protein_g: macroGramsFromPct(kcal, pp, 4),
        daily_fat_g: macroGramsFromPct(kcal, pf, 9),
        pct_carbs: pc,
        pct_protein: pp,
        pct_fat: pf,
        waist_cm: plan.waist_cm ?? null,
        hip_cm: plan.hip_cm ?? null,
        activity_level: plan.activity_level ?? null,
        activities: plan.activities ?? null,
      });
      setPlan(data);
      setPlanForm(data);
      toast.success(`Objetivo diario establecido: ${kcal} kcal (${pc}/${pp}/${pf})`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addMeal = async (e) => {
    e.preventDefault();
    const kcal = Number(meal.calories) || 0;
    let carbs = Number(meal.carbs_g) || 0;
    let protein = Number(meal.protein_g) || 0;
    let fat = Number(meal.fat_g) || 0;
    let estimated = false;
    // Auto-estimación: si hay kcal pero los 3 macros están en 0, usamos el split
    // configurado en el plan del atleta (fallback a 50/25/25).
    if (kcal > 0 && carbs === 0 && protein === 0 && fat === 0) {
      const pc = Number(plan.pct_carbs) || DEFAULT_MACRO_SPLIT.pct_carbs;
      const pp = Number(plan.pct_protein) || DEFAULT_MACRO_SPLIT.pct_protein;
      const pf = Number(plan.pct_fat) || DEFAULT_MACRO_SPLIT.pct_fat;
      carbs = macroGramsFromPct(kcal, pc, 4);
      protein = macroGramsFromPct(kcal, pp, 4);
      fat = macroGramsFromPct(kcal, pf, 9);
      estimated = true;
    }
    try {
      await api.post(`/users/${userId}/meals`, {
        date,
        meal_name: meal.meal_name,
        calories: kcal,
        carbs_g: carbs,
        protein_g: protein,
        fat_g: fat,
        meal_type: meal.meal_type || inferMealTypeFromNow(),
      });
      setMeal({ meal_name: "", calories: "", carbs_g: "", protein_g: "", fat_g: "", meal_type: inferMealTypeFromNow() });
      loadMeals();
      toast.success(estimated ? "Comida agregada (macros estimados)" : "Comida agregada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteMeal = async (id) => {
    try {
      await api.delete(`/meals/${id}`);
      loadMeals();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pickFromCatalog = (food) => {
    setSelectedFood(food);
    setServingG(food.serving_size_g);
    setSelectedMealType(inferMealTypeFromNow());
  };

  const handleBarcodeDetected = async (code) => {
    setScannerOpen(false);
    setScanLoading(true);
    try {
      const { data } = await api.get(`/food-barcode/${encodeURIComponent(code)}`);
      // Abrir picker + pre-seleccionar producto escaneado
      setPickerOpen(true);
      setSelectedFood(data);
      setServingG(data.serving_hint_g || data.serving_size_g || 100);
      setSelectedMealType(inferMealTypeFromNow());
      toast.success(`${data.name}${data.brand ? ` · ${data.brand}` : ""}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanLoading(false);
    }
  };

  const confirmCatalogSelection = async () => {
    if (!selectedFood) return;
    const baseServing = selectedFood.serving_size_g || 100;
    const grams = Math.max(1, Number(servingG) || 1);
    const factor = grams / baseServing;
    const label = selectedFood.brand ? `${selectedFood.name} · ${selectedFood.brand} (${grams}g)` : `${selectedFood.name} (${grams}g)`;
    try {
      await api.post(`/users/${userId}/meals`, {
        date,
        meal_name: label,
        calories: Number((selectedFood.calories * factor).toFixed(1)),
        carbs_g: Number((selectedFood.carbs_g * factor).toFixed(1)),
        protein_g: Number((selectedFood.protein_g * factor).toFixed(1)),
        fat_g: Number((selectedFood.fat_g * factor).toFixed(1)),
        meal_type: selectedMealType || inferMealTypeFromNow(),
      });
      toast.success(`${selectedFood.name} agregado`);
      // Recordar en catálogo personal con los gramos usados como nuevo hint
      rememberFood({ ...selectedFood, serving_hint_g: grams });
      setSelectedFood(null);
      setPickerOpen(false);
      loadMeals();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totals = meals.reduce((acc, m) => ({
    calories: acc.calories + (m.calories || 0),
    carbs: acc.carbs + (m.carbs_g || 0),
    protein: acc.protein + (m.protein_g || 0),
    fat: acc.fat + (m.fat_g || 0),
  }), { calories: 0, carbs: 0, protein: 0, fat: 0 });

  const calPct = plan.daily_calories > 0 ? Math.min(100, (totals.calories / plan.daily_calories) * 100) : 0;

  // Agrupa calorías consumidas por tipo de comida para la card de distribución.
  const kcalByType = meals.reduce((acc, m) => {
    const t = m.meal_type || "snack";
    acc[t] = (acc[t] || 0) + (m.calories || 0);
    return acc;
  }, {});

  // Alerta inteligente top-level: si ya consumió >80% del día antes de las 16hs.
  const nowHour = new Date().getHours();
  const viewingToday = date === today;
  const smartAlert = (() => {
    if (!viewingToday || plan.daily_calories <= 0) return null;
    const pct = (totals.calories / plan.daily_calories) * 100;
    if (nowHour < 16 && pct >= 80) {
      return { level: "warn", msg: `Ya consumiste el ${pct.toFixed(0)}% de las kcal del día y aún no son las 16:00. Guardá margen para la cena.` };
    }
    if (nowHour >= 21 && pct < 50) {
      return { level: "info", msg: `Solo llevás el ${pct.toFixed(0)}% de las kcal del día. Probá no acostarte con déficit grande.` };
    }
    if (pct > 120) {
      return { level: "danger", msg: `Superaste el objetivo diario en ${(pct - 100).toFixed(0)}%. Ajustá la próxima comida o sumá actividad.` };
    }
    return null;
  })();

  return (
    <div className="space-y-6">
      {/* Calculadora metabólica */}
      <MetabolicCalculator
        targetUser={targetUser}
        plan={plan}
        canEdit={isAdmin}
        onChange={saveMetabolicPatch}
        onUseAsGoal={useAsGoal}
      />
      {/* Plan */}
      <div className="card-base p-6" data-testid="nutrition-plan">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <h2 className="font-display font-black uppercase text-2xl">Plan Nutricional</h2>
          <div className="flex gap-2">
            <button onClick={() => exportNutritionPDF({ plan, meals, date, user: targetUser })} className="btn-secondary flex items-center gap-2" data-testid="export-nutrition-pdf">
              <FilePdf size={14} weight="bold" /> Exportar PDF
            </button>
            {isAdmin && !editingPlan && (
              <button onClick={() => setEditingPlan(true)} className="btn-secondary" data-testid="edit-plan-btn">Editar Plan</button>
            )}
          </div>
        </div>

        {editingPlan ? (
          <form onSubmit={savePlan} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Objetivo Calórico Diario</label>
              <input
                type="number" step="any"
                className="input-base max-w-xs"
                value={planForm.daily_calories ?? 0}
                onChange={(e) => setPlanForm({ ...planForm, daily_calories: e.target.value })}
                data-testid="plan-daily_calories"
              />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted mb-2 flex items-center justify-between">
                <span>Split de macronutrientes (%)</span>
                <span className={`text-[10px] ${
                  Math.abs((Number(planForm.pct_carbs) + Number(planForm.pct_protein) + Number(planForm.pct_fat)) - 100) > 0.5
                    ? "text-[#FF3B30]" : "text-[#32D74B]"
                }`} data-testid="macro-pct-sum">
                  Suma: {(Number(planForm.pct_carbs) + Number(planForm.pct_protein) + Number(planForm.pct_fat)).toFixed(0)}% {" "}
                  {Math.abs((Number(planForm.pct_carbs) + Number(planForm.pct_protein) + Number(planForm.pct_fat)) - 100) > 0.5 ? "(debe ser 100)" : "✓"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  ["pct_carbs", "Hidratos %", "#007AFF", 4],
                  ["pct_protein", "Proteínas %", "#FF3B30", 4],
                  ["pct_fat", "Grasas %", "#FFD60A", 9],
                ].map(([k, l, c, kpg]) => {
                  const kcal = Number(planForm.daily_calories) || 0;
                  const grams = macroGramsFromPct(kcal, Number(planForm[k]) || 0, kpg);
                  return (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: c }}>{l}</label>
                      <input
                        type="number" step="1" min="0" max="100"
                        className="input-base"
                        value={planForm[k] ?? 0}
                        onChange={(e) => setPlanForm({ ...planForm, [k]: e.target.value })}
                        data-testid={`plan-${k}`}
                      />
                      <div className="text-[10px] text-muted mt-1">= {grams}g</div>
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-muted mt-2">
                Ejemplo típico: 50% hidratos · 25% proteínas · 25% grasas. Para dietas de fuerza probar 40/35/25. Los gramos se calculan automáticamente.
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn-primary flex items-center gap-2" data-testid="save-plan-btn"><FloppyDisk size={16} weight="bold" />Guardar</button>
              <button type="button" onClick={() => setEditingPlan(false)} className="btn-secondary">Cancelar</button>
            </div>
          </form>
        ) : (
          <div>
            {plan.daily_calories > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted mb-1">Objetivo Calorías</div>
                    <div className="font-display font-black text-4xl text-primary-app">{plan.daily_calories}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted mb-1">
                      Hidratos <span className="text-muted">· {Number(plan.pct_carbs ?? DEFAULT_MACRO_SPLIT.pct_carbs).toFixed(0)}%</span>
                    </div>
                    <div className="font-display font-black text-4xl" style={{ color: "#007AFF" }}>{Math.round(plan.daily_carbs_g || 0)}g</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted mb-1">
                      Proteínas <span className="text-muted">· {Number(plan.pct_protein ?? DEFAULT_MACRO_SPLIT.pct_protein).toFixed(0)}%</span>
                    </div>
                    <div className="font-display font-black text-4xl" style={{ color: "#FF3B30" }}>{Math.round(plan.daily_protein_g || 0)}g</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted mb-1">
                      Grasas <span className="text-muted">· {Number(plan.pct_fat ?? DEFAULT_MACRO_SPLIT.pct_fat).toFixed(0)}%</span>
                    </div>
                    <div className="font-display font-black text-4xl" style={{ color: "#FFD60A" }}>{Math.round(plan.daily_fat_g || 0)}g</div>
                  </div>
                </div>

                {/* Alerta inteligente de distribución */}
                {smartAlert && (
                  <div
                    className={`mb-5 p-4 border flex items-start gap-3 ${
                      smartAlert.level === "danger" ? "border-[#FF3B30] bg-[#FF3B30]/10" :
                      smartAlert.level === "warn" ? "border-[#FF9500] bg-[#FF9500]/10" :
                      "border-[#007AFF] bg-[#007AFF]/10"
                    }`}
                    data-testid="smart-alert"
                  >
                    <div className="text-xl leading-none mt-0.5">
                      {smartAlert.level === "danger" ? "⚠" : smartAlert.level === "warn" ? "⚡" : "ℹ"}
                    </div>
                    <div className="flex-1 text-sm">{smartAlert.msg}</div>
                  </div>
                )}

                {/* Distribución por tipo de comida */}
                <div className="bg-[#0A0A0A] border border-app p-4 mb-5" data-testid="meal-distribution">
                  <div className="text-xs uppercase tracking-widest text-muted mb-3">Distribución calórica por comida · consumido vs sugerido</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {MEAL_DISTRIBUTION.map((m) => {
                      const target = Math.round((plan.daily_calories * m.pct) / 100);
                      const consumed = Math.round(kcalByType[m.key] || 0);
                      const adh = classifyMealAdherence(consumed, target);
                      const barPct = target > 0 ? Math.min(100, (consumed / target) * 100) : 0;
                      return (
                        <div key={m.key} className="bg-[#141414] border border-app p-3 relative" data-testid={`meal-dist-${m.key}`}>
                          <div className="flex items-start justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-widest text-muted">{m.label} · {m.pct}%</div>
                            <span
                              className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 leading-tight border"
                              style={{ color: adh.color, borderColor: adh.color }}
                              data-testid={`badge-${m.key}`}
                            >
                              {adh.label}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="font-display font-black text-2xl" style={{ color: adh.status === "empty" ? "#A3A3A3" : adh.color }}>{consumed}</span>
                            <span className="text-[11px] text-muted">/ {target} kcal</span>
                          </div>
                          <div className="h-1.5 bg-[#262626] mt-2 overflow-hidden">
                            <div className="h-full transition-all" style={{ width: `${barPct}%`, backgroundColor: adh.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-muted">Calorías del día</span>
                  <span className="font-display font-bold">
                    <span className="text-white">{totals.calories.toFixed(0)}</span>
                    <span className="text-muted"> / {plan.daily_calories} kcal ({calPct.toFixed(0)}%)</span>
                  </span>
                </div>
                <div className="h-3 bg-[#262626] overflow-hidden mb-5" data-testid="calories-bar">
                  <div className="h-full bg-[#FF3B30] transition-all" style={{ width: `${calPct}%` }} />
                </div>

                <div className="space-y-4">
                  <MacroBar label="Hidratos" current={totals.carbs} target={plan.daily_carbs_g} color="#007AFF" />
                  <MacroBar label="Proteínas" current={totals.protein} target={plan.daily_protein_g} color="#FF3B30" />
                  <MacroBar label="Grasas" current={totals.fat} target={plan.daily_fat_g} color="#FFD60A" />
                </div>
              </>
            ) : (
              <div className="text-muted">Aún no hay plan nutricional definido.</div>
            )}
          </div>
        )}
      </div>

      {/* Favoritos: top comidas frecuentes del atleta */}
      {(isAdmin || isOwner) && (
        <FrequentFoods items={frequents} onQuickAdd={quickAddFrequent} loading={frequentsLoading} />
      )}

      {/* Adherencia semanal por tipo de comida (insight) */}
      <WeeklyNutritionAdherence userId={userId} key={`adh-${meals.length}-${date}`} />

      {/* Meal tracker */}
      <div className="card-base p-6" data-testid="meals-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <h2 className="font-display font-black uppercase text-2xl">Comidas</h2>
          <input type="date" className="input-base !w-48" value={date} onChange={(e) => setDate(e.target.value)} data-testid="meals-date" />
        </div>

        {(isAdmin || isOwner) && (
          <>
            <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end flex-wrap">
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="open-food-catalog-btn"
              >
                <MagnifyingGlass size={14} weight="bold" /> Elegir del catálogo
              </button>
              <button
                type="button"
                onClick={() => setScannerOpen(true)}
                disabled={scanLoading}
                className="btn-secondary flex items-center justify-center gap-2 !border-[#FF3B30] text-[#FF3B30] hover:!bg-[#FF3B30] hover:text-white"
                data-testid="open-barcode-scanner-btn"
              >
                <Barcode size={14} weight="bold" /> {scanLoading ? "Buscando..." : "Escanear código"}
              </button>
              <span className="text-xs text-muted">o cargá manualmente ↓</span>
            </div>
            <form onSubmit={addMeal} className="space-y-3 mb-2" data-testid="meal-form">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[10px] uppercase tracking-widest text-muted">Tipo de comida:</span>
                {[
                  { k: "desayuno", l: "Desayuno" },
                  { k: "almuerzo", l: "Almuerzo" },
                  { k: "merienda", l: "Merienda" },
                  { k: "cena", l: "Cena" },
                  { k: "snack", l: "Snack" },
                ].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setMeal({ ...meal, meal_type: o.k })}
                    className={`text-[10px] uppercase tracking-widest font-bold border px-3 py-1.5 transition-colors ${
                      meal.meal_type === o.k
                        ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                        : "border-[#262626] text-muted hover:text-white"
                    }`}
                    data-testid={`meal-type-${o.k}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <input className="input-base md:col-span-2" placeholder="Comida" value={meal.meal_name} onChange={(e) => setMeal({ ...meal, meal_name: e.target.value })} required data-testid="meal-name" />
                <input type="number" step="any" className="input-base" placeholder="kcal" value={meal.calories} onChange={(e) => setMeal({ ...meal, calories: e.target.value })} required data-testid="meal-calories" />
                <input type="number" step="any" className="input-base" placeholder="Hidr." value={meal.carbs_g} onChange={(e) => setMeal({ ...meal, carbs_g: e.target.value })} data-testid="meal-carbs" />
                <input type="number" step="any" className="input-base" placeholder="Prot." value={meal.protein_g} onChange={(e) => setMeal({ ...meal, protein_g: e.target.value })} data-testid="meal-protein" />
                <input type="number" step="any" className="input-base" placeholder="Grasa" value={meal.fat_g} onChange={(e) => setMeal({ ...meal, fat_g: e.target.value })} data-testid="meal-fat" />
              </div>
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" data-testid="add-meal-btn">
                <Plus size={14} weight="bold" />Agregar comida
              </button>
            </form>
            <div className="text-[10px] text-muted mb-6">
              Si solo sabés las kcal, dejá Hidr./Prot./Grasa en blanco y los autocalculamos con distribución balanceada (50% hidratos · 25% proteínas · 25% grasas).
            </div>
          </>
        )}

        {meals.length === 0 ? (
          <div className="text-muted text-sm py-6 text-center">No hay comidas registradas este día</div>
        ) : (
          <div className="space-y-2" data-testid="meals-list">
            {meals.map((m) => {
              const mealLabel = MEAL_DISTRIBUTION.find((d) => d.key === m.meal_type)?.label || (m.meal_type === "snack" ? "Snack" : null);
              return (
                <div key={m.meal_id} className="flex items-center justify-between bg-[#0A0A0A] border border-app p-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {mealLabel && (
                        <span className="text-[9px] uppercase tracking-widest font-bold bg-[#262626] text-muted px-1.5 py-0.5 leading-tight" data-testid={`meal-type-tag-${m.meal_id}`}>
                          {mealLabel}
                        </span>
                      )}
                      <div className="font-display font-bold uppercase">{m.meal_name}</div>
                    </div>
                    <div className="text-xs text-muted mt-0.5">
                      {m.calories.toFixed(0)} kcal · H: {m.carbs_g.toFixed(0)}g · P: {m.protein_g.toFixed(0)}g · G: {m.fat_g.toFixed(0)}g
                    </div>
                  </div>
                  <button onClick={() => deleteMeal(m.meal_id)} className="text-muted hover:text-[#FF3B30]" data-testid={`delete-meal-${m.meal_id}`}>
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {pickerOpen && (
        <FoodCatalogPicker
          catalog={catalog}
          selected={selectedFood}
          servingG={servingG}
          setServingG={setServingG}
          mealType={selectedMealType}
          setMealType={setSelectedMealType}
          onPick={pickFromCatalog}
          onConfirm={confirmCatalogSelection}
          onClose={() => { setPickerOpen(false); setSelectedFood(null); }}
        />
      )}
      {scannerOpen && (
        <BarcodeScanner
          onDetected={handleBarcodeDetected}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </div>
  );
}

function FoodCatalogPicker({ catalog, selected, servingG, setServingG, mealType, setMealType, onPick, onConfirm, onClose }) {
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("all");
  const [mode, setMode] = useState("local"); // "local" | "online"
  const [onlineResults, setOnlineResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [onlineTouched, setOnlineTouched] = useState(false);
  const [onlineError, setOnlineError] = useState(null);

  const categories = useMemo(() => {
    const set = new Set(catalog.map((f) => f.category));
    return ["all", ...Array.from(set)];
  }, [catalog]);

  const localFiltered = catalog.filter((f) => {
    const matchCat = category === "all" || f.category === category;
    const matchName = f.name.toLowerCase().includes(filter.toLowerCase());
    return matchCat && matchName;
  });

  // Búsqueda remota con debounce
  useEffect(() => {
    if (mode !== "online") return;
    const q = filter.trim();
    if (q.length < 2) {
      setOnlineResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      setOnlineTouched(true);
      setOnlineError(null);
      try {
        const { data } = await api.get("/food-search", { params: { q } });
        setOnlineResults(data);
      } catch (err) {
        setOnlineResults([]);
        setOnlineError(err.response?.data?.detail || "No se pudo buscar ahora mismo");
      } finally {
        setSearching(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [filter, mode]);

  const displayed = mode === "online" ? onlineResults : localFiltered;

  const servingGNum = Number(servingG) || 0;
  const factor = selected ? servingGNum / (selected.serving_size_g || 100) : 0;
  const preview = selected
    ? {
        calories: (selected.calories * factor).toFixed(0),
        carbs_g: (selected.carbs_g * factor).toFixed(1),
        protein_g: (selected.protein_g * factor).toFixed(1),
        fat_g: (selected.fat_g * factor).toFixed(1),
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-stretch sm:items-center justify-center sm:p-4 food-picker-root"
      data-testid="food-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="card-base w-full sm:max-w-4xl flex flex-col food-picker-modal sm:!h-auto sm:max-h-[92dvh]"
        style={{ minHeight: "0" }}
      >
        <div className="p-4 sm:p-5 border-b border-app flex items-center justify-between flex-shrink-0">
          <h3 className="font-display font-black uppercase text-xl sm:text-2xl tracking-tighter">Catálogo de comidas</h3>
          <button onClick={onClose} className="text-muted hover:text-white" data-testid="close-food-picker">
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Search / filters: se ocultan cuando hay alimento seleccionado para liberar espacio vertical (UX mobile) */}
        {!selected && (
          <div className="p-4 border-b border-app space-y-3 flex-shrink-0">
          {/* Mode toggle */}
          <div className="inline-flex border border-app" data-testid="food-mode-toggle">
            <button
              type="button"
              onClick={() => { setMode("local"); setFilter(""); }}
              className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold transition-colors ${
                mode === "local" ? "bg-[#FF3B30] text-white" : "bg-transparent text-muted hover:text-white"
              }`}
              data-testid="food-mode-local"
            >
              Catálogo Athlon
            </button>
            <button
              type="button"
              onClick={() => { setMode("online"); setFilter(""); setOnlineResults([]); }}
              className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold transition-colors ${
                mode === "online" ? "bg-[#FF3B30] text-white" : "bg-transparent text-muted hover:text-white"
              }`}
              data-testid="food-mode-online"
            >
              Open Food Facts (productos AR)
            </button>
          </div>

          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              className="input-base pl-10"
              placeholder={mode === "online" ? "Buscar productos argentinos (milanesa, yogur, etc.)" : "Buscar alimento (pollo, arroz, palta...)"}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
              data-testid="food-search"
            />
          </div>

          {mode === "local" && (
            <div className="flex gap-2 flex-wrap">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={`text-[10px] uppercase tracking-widest font-bold border px-3 py-1.5 transition-colors ${
                    category === c ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
                  }`}
                  data-testid={`food-cat-${c}`}
                >
                  {c === "all" ? "Todos" : c}
                </button>
              ))}
            </div>
          )}
          {mode === "online" && (
            <div className="text-[10px] text-muted">
              Buscador en vivo sobre Open Food Facts (filtrado a Argentina). Los valores son por 100g · los productos incluyen marca cuando está disponible.
            </div>
          )}
          </div>
        )}

        {!selected && (
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 min-h-0">
          {mode === "online" && searching && (
            <div className="col-span-full text-center text-muted py-6 text-xs uppercase tracking-widest">Buscando...</div>
          )}
          {mode === "online" && onlineError && !searching && (
            <div className="col-span-full text-center py-6 text-xs text-[#FF3B30]" data-testid="food-online-error">
              {onlineError}
            </div>
          )}
          {displayed.length === 0 && !searching && !onlineError ? (
            <div className="col-span-full text-center text-muted py-12 text-sm">
              {mode === "online" && !onlineTouched ? "Empezá a escribir para buscar productos argentinos" : "Sin resultados"}
            </div>
          ) : displayed.map((f, idx) => {
            const isSel = selected && selected.name === f.name && selected.brand === f.brand;
            return (
              <button
                key={`${f.name}-${f.brand || ""}-${idx}`}
                type="button"
                onClick={() => onPick(f)}
                className={`card-base !p-4 text-left transition-colors relative ${isSel ? "!border-[#FF3B30]" : "hover:!border-white"}`}
                data-testid={`food-item-${f.name}`}
              >
                {f.image_url && (
                  <img src={f.image_url} alt="" className="w-full h-20 object-contain mb-2 bg-white" loading="lazy" />
                )}
                <div className="text-[10px] uppercase tracking-widest text-primary-app mb-1">
                  {f.category}{f.brand ? ` · ${f.brand}` : ""}
                </div>
                <div className="font-display font-bold uppercase text-sm leading-tight mb-2 line-clamp-2">{f.name}</div>
                <div className="text-[10px] text-muted">{f.serving_size_g}g · {f.calories} kcal</div>
                <div className="text-[10px] text-muted mt-1">H {f.carbs_g}g · P {f.protein_g}g · G {f.fat_g}g</div>
                {f.food_key && f.use_count > 1 && (
                  <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold bg-[#FFD60A] text-black px-1.5 leading-tight" title={`Agregado ${f.use_count} veces por la comunidad`}>
                    ×{f.use_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        )}

        {selected && (
          <div
            className="flex-1 border-t border-app bg-[#0A0A0A] flex flex-col min-h-0"
            style={{
              paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
            }}
            data-testid="food-selection-footer"
          >
            {/* Área scrolleable: meal type + seleccionado + gramos + macros */}
            <div className="p-4 overflow-y-auto min-h-0 flex-1" style={{ WebkitOverflowScrolling: "touch" }}>
              {/* Back */}
              <button
                type="button"
                onClick={() => onPick(null)}
                className="text-[10px] uppercase tracking-widest text-muted hover:text-white mb-3 flex items-center gap-1"
                data-testid="food-back-to-list"
              >
                ← Cambiar alimento
              </button>              {/* Selector de tipo de comida */}
              <div className="mb-3" data-testid="food-mealtype-row">
                <label className="text-[10px] uppercase tracking-widest text-muted block mb-2">Tipo de comida</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { k: "desayuno", l: "Desayuno" },
                    { k: "almuerzo", l: "Almuerzo" },
                    { k: "merienda", l: "Merienda" },
                    { k: "cena", l: "Cena" },
                    { k: "snack", l: "Snack" },
                  ].map((o) => {
                    const active = mealType === o.k;
                    return (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => setMealType && setMealType(o.k)}
                        className={`text-[11px] uppercase tracking-widest font-bold border px-3 py-2 transition-colors ${
                          active ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
                        }`}
                        data-testid={`food-mealtype-${o.k}`}
                      >
                        {o.l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4 mb-1">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Seleccionado{selected.brand ? ` · ${selected.brand}` : ""}</div>
                  <div className="font-display font-bold uppercase text-base sm:text-lg line-clamp-2">{selected.name}</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted block mb-1">Gramos</label>
                  <input
                    type="number"
                    min="1"
                    className="input-base !py-2 !w-28"
                    value={servingG === "" ? "" : servingG}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") { setServingG(""); return; }
                      const n = Number(v);
                      if (Number.isFinite(n)) setServingG(n);
                    }}
                    onBlur={() => {
                      if (servingG === "" || Number(servingG) < 1) setServingG(1);
                    }}
                    data-testid="food-serving-g"
                  />
                </div>
                <div className="grid grid-cols-4 gap-1.5 text-center">
                  <Macro label="kcal" value={preview.calories} color="#FFD60A" />
                  <Macro label="H" value={`${preview.carbs_g}g`} color="#007AFF" />
                  <Macro label="P" value={`${preview.protein_g}g`} color="#FF3B30" />
                  <Macro label="G" value={`${preview.fat_g}g`} color="#FFD60A" />
                </div>
              </div>
            </div>
            {/* Botón siempre visible, fuera del scroll */}
            <div className="px-4 pt-2 flex-shrink-0">
              <button
                type="button"
                onClick={onConfirm}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="confirm-food-selection"
              >
                <Plus size={14} weight="bold" /> Agregar al día
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Macro({ label, value, color }) {
  return (
    <div className="bg-[#141414] border border-app px-3 py-2 min-w-[60px]">
      <div className="text-[9px] uppercase tracking-widest text-muted">{label}</div>
      <div className="font-display font-black text-base" style={{ color }}>{value}</div>
    </div>
  );
}
